import { useState, useMemo } from 'react'
import { mockVendors } from '../data/mockVendors'
import VendorCard from '../components/VendorCard'
import { Vendor } from '../types'

export default function VendorDirectory() {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<'rating' | 'reviews' | 'name'>('rating')
  const [filterVerified, setFilterVerified] = useState(false)
  const [minRating, setMinRating] = useState(0)
  const [vendorTypeFilter, setVendorTypeFilter] = useState<'all' | '7oh' | 'mit' | 'pseudo' | 'plain-leaf'>('all')

  const filteredAndSortedVendors = useMemo(() => {
    const filtered = mockVendors.filter((vendor: Vendor) => {
      const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vendor.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           vendor.specialties.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
      
      const matchesVerified = !filterVerified || vendor.verified
      const matchesRating = vendor.rating >= minRating
      
      // Vendor type filter
      const specialtiesText = vendor.specialties.join(' ').toLowerCase()
      let matchesType = true
      if (vendorTypeFilter === '7oh') {
        matchesType = specialtiesText.includes('7oh') || specialtiesText.includes('7-oh')
      } else if (vendorTypeFilter === 'mit') {
        matchesType = specialtiesText.includes('mit') || specialtiesText.includes('mitragynine extract')
      } else if (vendorTypeFilter === 'pseudo') {
        matchesType = specialtiesText.includes('pseudo') || specialtiesText.includes('pseudoindoxyl')
      } else if (vendorTypeFilter === 'plain-leaf') {
        matchesType = specialtiesText.includes('red') || specialtiesText.includes('green') || specialtiesText.includes('white') || 
                     specialtiesText.includes('maeng da') || specialtiesText.includes('bali') || specialtiesText.includes('borneo') ||
                     specialtiesText.includes('bulk powder') || specialtiesText.includes('sample packs')
      }
      
      return matchesSearch && matchesVerified && matchesRating && matchesType
    })

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating
        case 'reviews':
          return b.reviewCount - a.reviewCount
        case 'name':
          return a.name.localeCompare(b.name)
        default:
          return 0
      }
    })
  }, [searchTerm, sortBy, filterVerified, minRating, vendorTypeFilter])

  return (
    <div className="vendor-directory">
      <div className="directory-header">
        <h1>Vendor Directory</h1>
        <p>Browse our comprehensive list of kratom vendors, from traditional leaf to 7OH products. All vendors are independently reviewed.</p>
      </div>

      <div className="search-filters">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search vendors, specialties, or descriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filters">
          <div className="filter-group">
            <label htmlFor="sort-by">Sort by:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'rating' | 'reviews' | 'name')}
              className="filter-select"
            >
              <option value="rating">Highest Rated</option>
              <option value="reviews">Most Reviews</option>
              <option value="name">Name (A-Z)</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="min-rating">Minimum Rating:</label>
            <select
              id="min-rating"
              value={minRating}
              onChange={(e) => setMinRating(Number(e.target.value))}
              className="filter-select"
            >
              <option value={0}>Any Rating</option>
              <option value={3}>3+ Stars</option>
              <option value={4}>4+ Stars</option>
              <option value={4.5}>4.5+ Stars</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="vendor-type">Vendor Type:</label>
            <select
              id="vendor-type"
              value={vendorTypeFilter}
              onChange={(e) => setVendorTypeFilter(e.target.value as 'all' | '7oh' | 'mit' | 'pseudo' | 'plain-leaf')}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="7oh">💊 7OH Specialists</option>
              <option value="mit">⚗️ MIT Extract</option>
              <option value="pseudo">🧬 Pseudoindoxyl</option>
              <option value="plain-leaf">🌿 Plain Leaf</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filterVerified}
                onChange={(e) => setFilterVerified(e.target.checked)}
              />
              Verified vendors only
            </label>
          </div>
        </div>
      </div>

      <div className="results-summary">
        <p>Showing {filteredAndSortedVendors.length} of {mockVendors.length} vendors</p>
      </div>

      <div className="vendor-grid">
        {filteredAndSortedVendors.map((vendor) => (
          <VendorCard key={vendor.id} vendor={vendor} />
        ))}
      </div>

      {filteredAndSortedVendors.length === 0 && (
        <div className="no-results">
          <p>No vendors found matching your criteria. Try adjusting your filters.</p>
        </div>
      )}
    </div>
  )
}